<template>
  <MeterpointHeader :meterpoint="meterpoint" class="mb-4" />
  <p class="mt-2 mb-1">Current Agreement</p>
  <SoWidget data-testid="current-tariff-widget">
    <div v-if="currentTariff" class="d-flex justify-space-between">
      <div class="mr-2">
        <div>{{ currentTariff.display }}</div>
        <SoChip
          v-for="(chip, index) in currentTariff.tariffChips"
          v-bind="chip"
          :key="`current-tariff-chip-${index}`"
          class="mr-1 mt-1"
        />
        <SoChip
          v-if="renewals.showRateChangeBanner && currentTariff.isVariable"
          data-testid="current-unit-rate-change-chip"
          v-bind="rateChangeChipProps"
          class="mr-1 mt-1"
        />
      </div>
      <div class="d-flex align-center">
        Annual Cost: {{ currentAgreementCost }}
      </div>
    </div>

    <div v-else>N/A</div>
  </SoWidget>

  <p class="mt-2 mb-1">New Agreement</p>
  <SoWidget
    data-testid="new-agreement-card-widget"
    :class="{ 'new-agreement-card--active': selectedTariffIsNotCurrentTariff }"
    class="new-agreement-card mb-1"
    tabindex="0"
    @click="showNewTariffs = !showNewTariffs"
    @keyup.enter="showNewTariffs = !showNewTariffs"
  >
    <div class="d-flex ml-n2 justify-space-between">
      <div class="d-flex align-center">
        <SoIcon
          :icon="showNewTariffs ? 'chevron-up' : 'chevron-down'"
          size="small"
        />
        <div v-if="selectedAgreement" class="ml-1 mr-2">
          <div>{{ selectedAgreement.display }}</div>
          <SoChip
            v-for="(chip, index) in selectedAgreement.tariffChips"
            v-bind="chip"
            :key="`selected-agreement-chip-${index}`"
            class="mr-1 mt-1"
          />
          <SoChip
            v-if="renewals.showRateChangeBanner && selectedAgreement.isVariable"
            data-testid="selected-unit-rate-change-chip"
            v-bind="rateChangeChipProps"
            class="mr-1 mt-1"
          />
        </div>
      </div>
      <div v-if="selectedAgreementCost" class="d-flex align-center">
        Annual Cost: {{ selectedAgreementCost }}
      </div>
    </div>
  </SoWidget>
  <SoWidget no-border>
    <template #expand>
      <div v-show="showNewTariffs">
        <div class="mt-4">
          <SoButton
            :title="`Calculate new ${meterpoint.eacOrAq} estimate`"
            type="link"
            @click="isUsageCalculatorModalShown = true"
          >
          </SoButton>
        </div>
        <div
          v-for="(register, index) in renewals.registerUsage[
            meterpoint.identifier
          ]"
          :key="index"
        >
          <div class="register-usage-container mt-2">
            <SoTextField
              v-model="register.usage"
              :prefix="meterpoint.eacOrAq"
              :label="
                register.rateName ? `Register  (${register.rateName})` : ''
              "
              suffix="kWh"
              type="number"
            />
          </div>
        </div>
        <h5 class="mt-5 mb-2">Current Tariff</h5>
        <SoDataTable
          :headers="tariffTableHeaders"
          :items="currentTariffTableData"
          :searchable="false"
          :has-pagination="false"
          show-copy-to-clipboard
        />
        <h5 class="mt-4">Available Tariffs</h5>
        <SoDataTable
          v-model="selectedTariffId"
          :headers="tariffTableHeaders"
          :items="importTariffsTableData"
          selectable
          select-key="id"
          row-clickable
          show-copy-to-clipboard
        />
        <SoCheckbox
          v-model="includeExclusives"
          label="Include exclusives"
          data-testid="so-checkbox"
        />
        <SoCheckbox
          v-model="showNoStandingChargeTariffs"
          label="Show No-Standing-Charge Tariffs"
        />
        <SoCheckbox v-model="showTariffRates" :label="showTariffRatesLabel" />
      </div>
    </template>
  </SoWidget>
  <UsageCalculatorModal
    v-model="isUsageCalculatorModalShown"
    :meterpoint="meterpoint"
  />
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import { IMeterpoint } from "@/models/meterpoint/meterpoint.types"
  import MeterpointHeader from "@/views/customer/components/MeterpointHeader.vue"
  import UsageCalculatorModal from "@/views/customer/renewals/modals/UsageCalculatorModal.vue"
  import { ITariff } from "@/models/tariff/tariff.types"
  import { ITariffAgreements } from "@/models/tariffAgreements/tariffAgreements.types"
  import {
    SoDataTableColumnType,
    SoDataTableHeader,
    SoDataTableItem,
  } from "@/types/components/SoDataTable"
  import { useRenewalsStore } from "@/stores/renewals"
  import { formatMoney, formatPriceInPence } from "@/helpers/moneyHelpers"
  import { rateChangeChipProps } from "@/helpers/unitRateChangeHelper"
  import { carriedVatRate, vatRatePercentLabel } from "@/helpers/vatHelper"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import SoChip from "@/components/SoChip.vue"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoWidget from "@/components/SoWidget.vue"

  export default defineComponent({
    components: {
      SoButton,
      SoDataTable,
      SoTextField,
      SoCheckbox,
      SoIcon,
      SoWidget,
      UsageCalculatorModal,
      MeterpointHeader,
      SoChip,
    },
    props: {
      meterpoint: {
        type: Object as PropType<IMeterpoint>,
        required: true,
      },
      meterpointTariffs: {
        type: Object as PropType<ITariffAgreements>,
        required: true,
      },
      currentTariff: {
        type: Object as PropType<ITariff | undefined>,
        default: undefined,
      },
    },
    data() {
      return {
        renewals: useRenewalsStore(),
        selectedTariffId: [
          useRenewalsStore().selectedTariffs[this.meterpoint.identifier]?.id,
        ],
        isUsageCalculatorModalShown: false,
        includeExclusives: false,
        showNoStandingChargeTariffs: false,
        showTariffRates: false,
        showNewTariffs: false,
        rateChangeChipProps,
        tariffFields: [
          {
            title: "Tariff",
            key: "tariff",
            type: SoDataTableColumnType.CHIPS,
          },
          { title: "Exit Fees", key: "exitFees" },
        ] as SoDataTableHeader[],
        ratesFields: [
          { title: "Standing Charge", key: "standingCharge" },
          { title: "Rates", key: "rates" },
        ] as SoDataTableHeader[],
      }
    },
    computed: {
      showTariffRatesLabel(): string {
        const rate = carriedVatRate([
          this.currentTariff,
          ...this.filteredTariffs,
        ])
        return rate === undefined
          ? "Show tariff rates (including VAT)"
          : `Show tariff rates (including ${vatRatePercentLabel(rate)} VAT)`
      },
      tariffTableHeaders() {
        return this.showTariffRates
          ? this.tariffFields.concat(this.ratesFields)
          : this.tariffFields
      },
      importTariffs() {
        return (
          this.meterpointTariffs.publiclyAvailableTariffList?.filter(
            (tariff: ITariff) => !tariff.isExport,
          ) ?? []
        )
      },
      filteredTariffs() {
        let tariffs = this.importTariffs
        if (!this.includeExclusives) {
          tariffs = tariffs.filter((tariff: ITariff) => !tariff.isExclusive)
        }
        if (this.showNoStandingChargeTariffs) {
          tariffs = tariffs.filter((tariff: ITariff) => tariff.isNSC)
        } else {
          tariffs = tariffs.filter((tariff: ITariff) => !tariff.isNSC)
        }
        return tariffs
      },
      selectedAgreement() {
        return this.renewals.selectedTariffs[this.meterpoint.identifier]
      },
      selectedTariffIsNotCurrentTariff() {
        return this.selectedTariffId[0] !== this.currentTariff?.id
      },
      importTariffsTableData() {
        return this.filteredTariffs.map(this.createTariffTableData)
      },
      currentTariffTableData() {
        if (!this.currentTariff) return []
        return [this.createTariffTableData(this.currentTariff)]
      },
      currentAgreementCost() {
        return formatMoney({
          amount:
            this.renewals.annualCosts[this.meterpoint.identifier]
              .currentAgreementCost,
          showNegativeSign: false,
          round: false,
        })
      },
      selectedAgreementCost() {
        return formatMoney({
          amount:
            this.renewals.annualCosts[this.meterpoint.identifier]
              ?.selectedAgreementCost ?? 0,
          showNegativeSign: false,
          round: false,
        })
      },
    },
    watch: {
      selectedTariffId(val) {
        if (val.length) {
          this.renewals.updateAgreement(this.meterpoint.identifier, val[0])
        }
      },
    },
    methods: {
      createTariffTableData(tariff: ITariff) {
        return {
          id: tariff.id,
          tariff: {
            value: tariff.display,
            props: [...tariff.tariffChips, ...tariff.tariffTypeChips],
            tooltip: tariff.description,
          },
          annualCost: formatMoney({
            amount:
              this.renewals.annualCosts[this.meterpoint.identifier]
                ?.importTariffCosts[tariff.id],
            showNegativeSign: false,
            round: false,
          }),
          exitFees: formatMoney({
            amount: tariff.productDetails.earlyTerminationCharge,
            showNegativeSign: false,
          }),
          standingCharge: `${formatPriceInPence(
            tariff.productDetails.standingCharge,
          )}/day`,
          rates: tariff.unitRatesFormatted,
        } as SoDataTableItem
      },
    },
  })
</script>

<style scoped>
  .register-usage-container {
    width: 200px;
  }

  .new-agreement-card {
    padding-left: 0;
    cursor: pointer;
  }
  .new-agreement-card--active {
    outline: solid 1px rgb(var(--v-theme-success));
    border-radius: var(--theme-radius);
  }

  .new-agreement-card:focus-visible {
    outline: var(--theme-outline);
    border-radius: var(--theme-radius);
  }
</style>
